import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { PermissionKey } from '@adisys/shared';
import { useAuth } from './lib/auth';
import { ADMIN_NAV_PERMISSIONS, AppShell } from './components/AppShell';
import { Button, EmptyState } from './components/ui';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { EmployeesPage } from './pages/Employees';
import { EmployeeDetailPage } from './pages/EmployeeDetail';
import { ProjectsPage } from './pages/Projects';
import { ProjectDetailPage } from './pages/ProjectDetail';
import { WorkPage } from './pages/Work';
import { ProductivityPage } from './pages/Productivity';
import { ExpensesPage } from './pages/Expenses';
import { ExpenseDetailPage } from './pages/ExpenseDetail';
import { ApprovalsPage } from './pages/Approvals';
import { LeaveCalendarPage } from './pages/LeaveCalendar';
import { LeaveRequestsPage } from './pages/LeaveRequests';
import { ReportsPage } from './pages/Reports';
import { SettingsPage } from './pages/Settings';
import { RolesPage } from './pages/Roles';
import { AuditPage } from './pages/Audit';
import { NotificationsPage } from './pages/Notifications';
import { AccountPage } from './pages/Account';
import { CrmDashboardPage } from './pages/crm/CrmDashboard';
import { LeadsPage } from './pages/crm/Leads';
import { LeadDetailPage } from './pages/crm/LeadDetail';
import { CrmProjectsPage } from './pages/crm/CrmProjects';
import { CrmProjectDetailPage } from './pages/crm/CrmProjectDetail';
import { CatalogPage } from './pages/crm/Catalog';
import { ProcurementPage } from './pages/crm/Procurement';
import { PurchaseOrderDetailPage } from './pages/crm/PurchaseOrderDetail';
import { FinancePage } from './pages/crm/Finance';
import { InvoiceDetailPage } from './pages/crm/InvoiceDetail';
import { InvoiceDocumentPage } from './pages/crm/InvoiceDocument';
import { ChallanDocumentPage } from './pages/crm/ChallanDocument';
import { QuotationDocumentPage } from './pages/crm/QuotationDocument';
import { CrmReportsPage } from './pages/crm/CrmReports';
import { PricingAccessPage } from './pages/crm/PricingAccess';
import { ProposalSettingsPage } from './pages/crm/ProposalSettings';
import { PublicProposalPage } from './pages/PublicProposal';

/**
 * Where the field app lives, for pointing people at it. In development
 * the two run side by side on localhost; in production it is configured.
 * If neither applies we say where to go without inventing a link.
 */
function fieldAppUrl(): string | null {
  const configured = import.meta.env.VITE_FIELD_APP_URL as string | undefined;
  if (configured) return configured;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:5174`;
  return null;
}

/**
 * Shown to an account that holds none of the portal's permissions —
 * field staff, in practice. They used to land on an unguarded dashboard
 * with an empty menu and no explanation, which looks like the product is
 * broken rather than like they are in the wrong place.
 */
function WrongPortal({ name, roleName, onSignOut }: {
  name: string; roleName: string; onSignOut: () => void;
}) {
  const url = fieldAppUrl();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface p-4">
      <div className="w-full max-w-md rounded-card bg-card p-6 shadow-card ring-1 ring-line">
        <h1 className="text-xl font-semibold text-ink-900">This portal is for managers</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-700">
          You are signed in as <strong>{name}</strong> ({roleName}). That role does its work in the
          ADISYS field app — recording time, submitting expenses and applying for leave — so there
          is nothing for it here.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {url
            ? <Button variant="primary" onClick={() => { window.location.href = url; }}>
                Open the field app
              </Button>
            : <p className="text-sm text-ink-600">Open the ADISYS field app on your phone instead.</p>}
          <Button onClick={onSignOut}>Sign out</Button>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-ink-500">
          If you were expecting access to this portal, ask your administrator to check your role.
        </p>
      </div>
    </div>
  );
}

function FullPageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface">
      <Loader2 className="h-6 w-6 animate-spin text-brand-500" aria-label="Loading" />
    </div>
  );
}

/** Blocks a route the caller's role does not include. */
function Guard({ permissions, children }: { permissions: PermissionKey[]; children: React.ReactNode }) {
  const { can } = useAuth();
  if (!can(...permissions)) {
    return (
      <EmptyState
        title="You do not have access to this area"
        description="Your ADISYS role does not include this module. If you believe this is wrong, contact your administrator."
      />
    );
  }
  return <>{children}</>;
}

/** The landing page depends on which part of the product the account works in. */
function Home() {
  const { can } = useAuth();
  if (can('dashboard.view')) return <DashboardPage />;
  if (can('crm.dashboard.view')) return <Navigate to="/crm" replace />;
  return <EmptyState title="You do not have access to this area"
    description="Your ADISYS role does not include this module. If you believe this is wrong, contact your administrator." />;
}

const CRM_ANY: PermissionKey[] = ['crm.dashboard.view', 'crm.lead.view.own', 'crm.lead.view.all', 'crm.project.view.own',
  'crm.project.view.all', 'crm.catalog.view', 'crm.procurement.view', 'crm.finance.view'];

export function App() {
  const { user, loading, can, signOut } = useAuth();
  const location = useLocation();

  // The customer's emailed link: no account, no session.
  if (location.pathname.startsWith('/p/')) {
    return <Routes><Route path="/p/:token" element={<PublicProposalPage />} /></Routes>;
  }

  if (loading) return <FullPageSpinner />;

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  // No menu item is open to this account, so the shell would render an
  // empty sidebar over a dashboard it cannot load. Say so instead.
  if (!can(...ADMIN_NAV_PERMISSIONS)) {
    return <WrongPortal name={user.fullName} roleName={user.roleName} onSignOut={() => void signOut()} />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="crm/quotations/:id/document" element={<Guard permissions={['crm.project.view.own', 'crm.project.view.all']}><QuotationDocumentPage /></Guard>} />
      <Route path="crm/finance/invoices/:id/document" element={<Guard permissions={['crm.finance.view']}><InvoiceDocumentPage /></Guard>} />
      <Route path="crm/finance/challans/:id/document" element={<Guard permissions={['crm.finance.view']}><ChallanDocumentPage /></Guard>} />
      <Route element={<AppShell />}>
        {/* Guarded like every other route: it was the one page anybody
            could land on regardless of permission. */}
        <Route index element={<Home />} />
        <Route path="work" element={<Guard permissions={['work.view.team', 'work.view.all']}><WorkPage /></Guard>} />
        <Route path="productivity" element={<Guard permissions={['productivity.view.team', 'productivity.view.all']}><ProductivityPage /></Guard>} />
        <Route path="projects" element={<Guard permissions={['project.view.managed', 'project.view.all', 'project.view.assigned']}><ProjectsPage /></Guard>} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="employees" element={<Guard permissions={['employee.view.team', 'employee.view.all']}><EmployeesPage /></Guard>} />
        <Route path="employees/:id" element={<EmployeeDetailPage />} />
        <Route path="expenses" element={<Guard permissions={['expense.view.own', 'expense.view.team', 'expense.view.all']}><ExpensesPage /></Guard>} />
        <Route path="expenses/:id" element={<ExpenseDetailPage />} />
        <Route path="approvals" element={<Guard permissions={['expense.approve.manager', 'expense.approve.finance']}><ApprovalsPage /></Guard>} />
        <Route path="leave/calendar" element={<Guard permissions={['leave.view.team', 'leave.view.all']}><LeaveCalendarPage /></Guard>} />
        <Route path="leave/requests" element={<Guard permissions={['leave.view.team', 'leave.view.all']}><LeaveRequestsPage /></Guard>} />
        <Route path="reports" element={<Guard permissions={['report.view.own', 'report.view.team', 'report.view.all']}><ReportsPage /></Guard>} />
        <Route path="settings" element={<Guard permissions={['settings.view']}><SettingsPage /></Guard>} />
        <Route path="roles" element={<Guard permissions={['rbac.manage']}><RolesPage /></Guard>} />
        <Route path="audit" element={<Guard permissions={['audit.view']}><AuditPage /></Guard>} />
        {/* ---- CRM ---------------------------------------------------- */}
        <Route path="crm" element={<Guard permissions={CRM_ANY}><CrmDashboardPage /></Guard>} />
        <Route path="crm/leads" element={<Guard permissions={['crm.lead.view.own', 'crm.lead.view.all']}><LeadsPage /></Guard>} />
        <Route path="crm/leads/:id" element={<Guard permissions={['crm.lead.view.own', 'crm.lead.view.all']}><LeadDetailPage /></Guard>} />
        <Route path="crm/projects" element={<Guard permissions={['crm.project.view.own', 'crm.project.view.all']}><CrmProjectsPage /></Guard>} />
        <Route path="crm/projects/:id" element={<Guard permissions={['crm.project.view.own', 'crm.project.view.all']}><CrmProjectDetailPage /></Guard>} />
        <Route path="crm/catalog" element={<Guard permissions={['crm.catalog.view']}><CatalogPage /></Guard>} />
        <Route path="crm/procurement" element={<Guard permissions={['crm.procurement.view']}><ProcurementPage /></Guard>} />
        <Route path="crm/procurement/purchase-orders/:id" element={<Guard permissions={['crm.procurement.view']}><PurchaseOrderDetailPage /></Guard>} />
        <Route path="crm/finance" element={<Guard permissions={['crm.finance.view']}><FinancePage /></Guard>} />
        <Route path="crm/finance/invoices/:id" element={<Guard permissions={['crm.finance.view']}><InvoiceDetailPage /></Guard>} />
        <Route path="crm/reports" element={<Guard permissions={['crm.report.view']}><CrmReportsPage /></Guard>} />
        <Route path="crm/pricing-access" element={<Guard permissions={['rbac.manage']}><PricingAccessPage /></Guard>} />
        <Route path="crm/proposal-settings" element={<Guard permissions={['settings.manage']}><ProposalSettingsPage /></Guard>} />        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={
          <EmptyState title="Page not found"
            description="The page you asked for does not exist. Use the navigation on the left to continue." />
        } />
      </Route>
    </Routes>
  );
}
